// Util file to help override the default values of the feature gate bootstrap values for lambda edge work
import { getSelectorsByUserAgent } from "react-device-detect";
import { UserAttributes } from "src/feature-gates/bootstrapper";
import { parseRoute } from "src/wac-routes";

import { ATL_COHORT } from "@/analytics/atl-analytics/cohort";
import { Cookie } from "@/utils/cookie";

const DEVICE_TYPE = "deviceType";

// TODO: GWP-5370 Remove this and refactor getCustomAttributes in bootstrapper.ts instead
/**
 * Does same thing as getCustomAttributes from bootstrapper.ts but for the client side. Used to help override the default values of the feature gate bootstrap values for lambda edge work
 *
 * @returns { UserAttributes }
 * */
export const getCustomAttributesOnClient = (): UserAttributes => {
    const data: Partial<UserAttributes> = {};
    const atlCohort = Cookie.readCookie(ATL_COHORT);
    if (atlCohort) {
        data[ATL_COHORT] = JSON.parse(atlCohort).bucketAll.index;
    }
    const userAgent = navigator.userAgent;
    const {
        isTablet,
        isMobile,
        isDesktop,
        browserName,
        browserVersion,
        osName
    } = getSelectorsByUserAgent(userAgent) ?? {};
    if (isTablet) {
        data[DEVICE_TYPE] = "tablet";
    } else if (isMobile) {
        data[DEVICE_TYPE] = "mobile";
    } else if (isDesktop) {
        data[DEVICE_TYPE] = "desktop";
    } else {
        data[DEVICE_TYPE] = "other";
    }
    data.platform = "uip";
    data.browserName = browserName;
    data.browserVersion = browserVersion;
    data.browserOS = osName;
    data.queryParams = location.search;
    data.requestPath = location.pathname;
    data.referer = document.referrer;
    data.locale = parseRoute(location).locale;
    const cloudSessionToken =
        Cookie.readCookie("cloud.session.token") ||
        Cookie.readCookie("cloud.session.token.stg");
    data.loginUserDetected = cloudSessionToken !== null;
    return data as UserAttributes;
};

interface FeatureGateBootstrapValues {
    identifiers?: {
        analyticsAnonymousId?: string;
        atlassianAccountId?: string;
    };
    experimentValues?: {
        user?: {
            customIDs?: {
                analyticsAnonymousId?: string;
                atlassianAccountId?: string;
            };
            custom?: Record<string, string | number | boolean>;
        };
        evaluated_keys?: {
            customIDs?: {
                analyticsAnonymousId?: string;
                atlassianAccountId?: string;
            };
        };
    };
    customAttributes?: Record<string, string | number | boolean>;
}

export function overrideAjsAnonIdFromCookie() {
    const ajsAnonIdFromCookie = Cookie.readCookie("ajs_anonymous_id");
    const featureGateBootstrapValues =
        window.featureGateBootstrapValues as FeatureGateBootstrapValues;

    if (ajsAnonIdFromCookie) {
        const decodedAjsAnonId = decodeURIComponent(
            ajsAnonIdFromCookie
        ).replace(/['"]+/g, "");

        if (featureGateBootstrapValues?.identifiers) {
            featureGateBootstrapValues.identifiers.analyticsAnonymousId =
                decodedAjsAnonId;
        } else {
            console.warn(
                "Could not set analyticsAnonymousId in identifiers - Object path is incomplete."
            );
        }

        if (featureGateBootstrapValues?.experimentValues?.user?.customIDs) {
            featureGateBootstrapValues.experimentValues.user.customIDs.analyticsAnonymousId =
                decodedAjsAnonId;
        } else {
            console.warn(
                "Could not set analyticsAnonymousId inside experimentValues.user.customIDs - Object path is incomplete."
            );
        }

        if (
            featureGateBootstrapValues?.experimentValues?.evaluated_keys
                ?.customIDs
        ) {
            featureGateBootstrapValues.experimentValues.evaluated_keys.customIDs.analyticsAnonymousId =
                decodedAjsAnonId;
        } else {
            console.warn(
                "Could not set analyticsAnonymousId inside experimentValues.evaluated_keys - Object path is incomplete."
            );
        }
    }
}

export function overrideAtlassianAccountIdFromCookie() {
    const atlassianAccountIdFromCookie = Cookie.readCookie("__aid_user_id");
    const featureGateBootstrapValues =
        window.featureGateBootstrapValues as FeatureGateBootstrapValues;

    if (atlassianAccountIdFromCookie) {
        if (featureGateBootstrapValues?.identifiers) {
            featureGateBootstrapValues.identifiers.atlassianAccountId =
                decodeURIComponent(atlassianAccountIdFromCookie);
        } else {
            console.warn(
                "Could not set atlassianAccountId in identifiers - Object path is incomplete."
            );
        }

        if (featureGateBootstrapValues?.experimentValues?.user?.customIDs) {
            featureGateBootstrapValues.experimentValues.user.customIDs.atlassianAccountId =
                decodeURIComponent(atlassianAccountIdFromCookie);
        } else {
            console.warn(
                "Could not set atlassianAccountId inside experimentValues.user.customIDs - Object path is incomplete."
            );
        }
    }
}

export function overrideCustomAttributes() {
    const newCustomAttributes = getCustomAttributesOnClient();
    const featureGateBootstrapValues =
        window.featureGateBootstrapValues as FeatureGateBootstrapValues;

    if (newCustomAttributes && featureGateBootstrapValues.customAttributes) {
        Object.assign(
            featureGateBootstrapValues.customAttributes,
            newCustomAttributes
        );
    } else {
        console.warn(
            "Could not set customAttributes inside featureGateBootstrapValues.customAttributes - Object path is incomplete."
        );
    }

    if (
        newCustomAttributes &&
        featureGateBootstrapValues?.experimentValues?.user?.custom
    ) {
        Object.assign(
            featureGateBootstrapValues.experimentValues.user.custom,
            newCustomAttributes
        );
    } else {
        console.warn(
            "Could not set customAttributes inside experimentValues.user.custom - Object path is incomplete."
        );
    }
}

const baseEdgePaths = ["/", "/software/jira/product-discovery"];

// Pre-compile the regex for customer paths
const CUSTOMERS_REGEX = new RegExp(`^(/[a-z]{2})?/customers(/.*)?$`);

function generateEdgeCachingPaths(edgePaths: string[]): string[] {
    const locales = [
        "br",
        "de",
        "en",
        "es",
        "fr",
        "it",
        "ja",
        "ko",
        "nl",
        "pl",
        "ru",
        "zh"
    ];
    const allPaths = new Set<string>();

    edgePaths.forEach((path) => {
        // Add the original path
        allPaths.add(path);

        // For root ("/"), just add /ja, /br, etc.
        if (path === "/") {
            locales.forEach((locale) => {
                allPaths.add(`/${locale}`);
            });
        } else {
            // Add localized versions like /ja/customers
            locales.forEach((locale) => {
                allPaths.add(`/${locale}${path}`);
            });
        }
    });

    return Array.from(allPaths).sort();
}

export const edgeCachingPaths = generateEdgeCachingPaths(baseEdgePaths);

export function isEdgeCachingPath(path: string): boolean {
    // First check if it's a customer path (fast regex check)
    if (CUSTOMERS_REGEX.test(path)) {
        return true;
    }

    // Then check other paths
    return edgeCachingPaths.includes(path);
}
